import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Col, Container, Row, Table } from 'reactstrap';
import { Breadcrumbs } from '../../components/Common';
import Spinner from '../../components/Common/Spinner';
import useAuthUser from '../../common/hooks/authUser';
import { isDealerPortalNavFromInventory } from '../../common/dealerPortalNavigation';
import type { TDealerPortalNavState } from '../../common/dealerPortalNavigation';
import { toastError } from '../../helpers/toast-helper';
import { getFormattedCurrency } from '../../helpers/currency-helper';
import { getTableFormattedDate } from '../../helpers/date-time-helper';
import { normalizeCustomerNumberForLookup } from '../../helpers/dealer-customer-input';
import { getDealerPortalInvoiceDetail } from '../../api/dealerPortal';
import type { TSyteLineInvoice } from '../../api/dealerPortal/types';

function formatInvoiceDate(value: unknown): string {
	if (value == null || value === '') return '—';
	const s = typeof value === 'string' ? value : String(value);
	return getTableFormattedDate(s) || '—';
}

const DealerInvoiceDetailsPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const redirectDoneRef = useRef(false);
	const { invoiceNumber: invoiceNumberParam } = useParams<{ invoiceNumber: string }>();
	const [searchParams] = useSearchParams();
	const customerNumber = useMemo(
		() => normalizeCustomerNumberForLookup(searchParams.get('customerNumber') ?? ''),
		[searchParams],
	);
	const invoiceNumber = invoiceNumberParam ?? '';

	const { user, isAdmin, isDataLoading } = useAuthUser();
	const [invoice, setInvoice] = useState<TSyteLineInvoice | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		document.title = 'Invoice details';
	}, []);

	useEffect(() => {
		if (user && !isAdmin) {
			toastError('Access denied. Admin privileges required.');
			navigate('/');
		}
	}, [user, isAdmin, navigate]);

	useEffect(() => {
		if (!user || !isAdmin) return;
		const allowed = isDealerPortalNavFromInventory(location.state);
		if (!allowed || !customerNumber || !invoiceNumber) {
			if (!redirectDoneRef.current) {
				redirectDoneRef.current = true;
				toastError('Open invoice details from Dealer inventory after selecting a dealer.');
				navigate('/admin/dealer-inventory', { replace: true });
			}
			setIsLoading(false);
			return;
		}
	}, [user, isAdmin, location.state, customerNumber, invoiceNumber, navigate]);

	useEffect(() => {
		if (!isDealerPortalNavFromInventory(location.state)) {
			setIsLoading(false);
			return;
		}
		if (!invoiceNumber || !customerNumber) {
			setIsLoading(false);
			setInvoice(null);
			return;
		}
		let cancelled = false;
		(async () => {
			setIsLoading(true);
			try {
				const data = await getDealerPortalInvoiceDetail(invoiceNumber, customerNumber);
				if (cancelled) return;
				if (data.errorMessage) {
					toastError(data.errorMessage);
					setInvoice(null);
				} else {
					setInvoice(data);
				}
			} catch (e) {
				if (!cancelled) {
					toastError(e instanceof Error ? e.message : 'Failed to load invoice.');
					setInvoice(null);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [invoiceNumber, customerNumber, location.state]);

	if (isDataLoading) {
		return <Spinner />;
	}

	if (user && !isAdmin) {
		return null;
	}

	if (!isDealerPortalNavFromInventory(location.state) || !customerNumber || !invoiceNumber) {
		return null;
	}

	const navState = { fromDealerInventory: true } satisfies TDealerPortalNavState;
	const backHref = `/admin/invoices?${new URLSearchParams({ customerNumber }).toString()}`;
	const lines = invoice?.invoiceLines ?? [];

	return (
		<Container fluid>
			<Breadcrumbs title="Invoice details" />
			<Row className="mb-3">
				<Col>
					<Button tag={Link} to={backHref} state={navState} color="light" className="mb-2">
						← Back to invoices
					</Button>
				</Col>
			</Row>

			{isLoading ? (
				<Spinner />
			) : !invoice ? (
				<p className="text-muted">Invoice could not be loaded.</p>
			) : (
				<>
					<Row className="mb-3">
						<Col md={8}>
							<dl className="row mb-0 small">
								<dt className="col-sm-4">Invoice #</dt>
								<dd className="col-sm-8">{invoice.invoiceNumber ?? '—'}</dd>
								<dt className="col-sm-4">Customer</dt>
								<dd className="col-sm-8">
									{invoice.customerName ?? '—'}{' '}
									<span className="text-muted">({invoice.customerNumber ?? '—'})</span>
								</dd>
								<dt className="col-sm-4">Invoice date</dt>
								<dd className="col-sm-8">{formatInvoiceDate(invoice.invoiceDate)}</dd>
								<dt className="col-sm-4">Due date</dt>
								<dd className="col-sm-8">{formatInvoiceDate(invoice.dueDate)}</dd>
								<dt className="col-sm-4">Order #</dt>
								<dd className="col-sm-8">{invoice.orderNumber ?? '—'}</dd>
								<dt className="col-sm-4">Customer PO</dt>
								<dd className="col-sm-8">{invoice.customerPO ?? '—'}</dd>
								<dt className="col-sm-4">Total amount</dt>
								<dd className="col-sm-8">{getFormattedCurrency(invoice.invoiceAmount)}</dd>
								<dt className="col-sm-4">Freight</dt>
								<dd className="col-sm-8">{getFormattedCurrency(invoice.freightAmount)}</dd>
							</dl>
						</Col>
					</Row>

					<h5 className="mb-2">Lines</h5>
					<div className="table-responsive">
						<Table bordered size="sm" className="align-middle">
							<thead className="table-light">
								<tr>
									<th>Line #</th>
									<th>Item</th>
									<th>Description</th>
									<th className="text-end">Qty</th>
									<th className="text-end">Price</th>
								</tr>
							</thead>
							<tbody>
								{lines.length === 0 && (
									<tr>
										<td colSpan={5} className="text-muted">
											No line items
										</td>
									</tr>
								)}
								{lines.map((line, lineIdx) => (
									<tr key={`${line.orderLineNumber ?? ''}-${lineIdx}`}>
										<td>{line.orderLineNumber ?? '—'}</td>
										<td>{line.item ?? '—'}</td>
										<td>{line.description ?? '—'}</td>
										<td className="text-end">{line.quantity?.toLocaleString() ?? '—'}</td>
										<td className="text-end">{getFormattedCurrency(line.price)}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</div>
				</>
			)}
		</Container>
	);
};

export default DealerInvoiceDetailsPage;
